"use client";
import { FC, ReactNode, useState } from "react";
import { Input } from "@/app/components/shadcn/input";
import { Button } from "@/app/components/shadcn/button";
import { Label } from "@/app/components/shadcn/label";
import { Checkbox } from "@/app/components/shadcn/checkbox";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/app/components/shadcn/select";
import { Search } from "lucide-react";

interface FilterSelectOption {
  id: string | number;
  label: string;
  count: number;
  component?: ReactNode;
}

export interface FilterSelectButton {
  label: string;
  onClick: () => void;
  variant?: "default" | "outline";
}

export interface FilterSelectProps {
  placeholder: string;
  options: FilterSelectOption[];
  selectedValues: (string | number)[];
  onSelectionChange: (value: string | number) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isActive: boolean;
  className?: string;
  contentWidth?: string;
  buttons?: FilterSelectButton[];
  showSearch?: boolean;
  searchPlaceholder?: string;
}

const FilterSelect: FC<FilterSelectProps> = ({
  placeholder,
  options,
  selectedValues,
  onSelectionChange,
  isOpen,
  onOpenChange,
  isActive,
  className = "",
  contentWidth = "w-64",
  buttons = [],
  showSearch = true,
  searchPlaceholder = "Ara...",
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <Select open={isOpen} onOpenChange={onOpenChange}>
      <SelectTrigger
        className={`h-10 min-w-24 transition-[color,box-shadow,background-color] ${
          isActive
            ? "border-black bg-gray-100 text-black font-medium [&>span]:text-black [&>span]:font-medium [&_svg]:text-black [&_svg]:opacity-100"
            : ""
        } ${className}`}
      >
        <SelectValue placeholder={placeholder}>
          <span>{placeholder}</span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent className={contentWidth}>
        {showSearch && (
          <div className="p-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        )}
        <div className="px-4 pb-2">
          <div className="max-h-60 overflow-y-auto space-y-3">
            {filteredOptions.map((option) => (
              <Label
                key={option.id}
                className="flex items-center gap-3 cursor-pointer py-1"
              >
                <Checkbox
                  checked={selectedValues.includes(option.id)}
                  onCheckedChange={() => onSelectionChange(option.id)}
                />
                {option.component && (
                  <div className="flex items-center gap-1">
                    {option.component}
                  </div>
                )}
                {/* Sayaç 0 ise parantez de basılmaz — "Beklemede (0)" yerine
                    sadece "Beklemede". */}
                <span className="text-sm">
                  {option.count > 0
                    ? `${option.label} (${option.count})`
                    : option.label}
                </span>
              </Label>
            ))}
          </div>
        </div>
        {buttons.length > 0 && (
          <div className="flex gap-3 p-4 border-t">
            {buttons.map((button, index) => (
              <Button
                key={index}
                variant={button.variant || "outline"}
                size="sm"
                className="flex-1"
                onClick={button.onClick}
              >
                {button.label}
              </Button>
            ))}
          </div>
        )}
      </SelectContent>
    </Select>
  );
};

export default FilterSelect;
